//crop-tracker-page.tsx

import React from "react";
import { useHistory } from "react-router-dom";
import Page from "../components/page";
import { useEffect, useLayoutEffect, useState, useContext } from "react";
import { cropTrackerApi } from "../services/api/crop-tracker";
import { CropTrackerRow } from "../models/crop-tracker";
import { ErrorContext } from "../components/app-routes";
import { StripedDataGrid } from "../components/striped-data-grid";
import LoadingBackdrop from "../components/loading-backdrop";
import {
  GridColDef,
  GridRowSelectionModel,
  GridToolbar,
  GRID_CHECKBOX_SELECTION_COL_DEF,
} from "@mui/x-data-grid";
import { Chip } from "@mui/material";
import { DateTime } from "luxon";
import {
  Box,
  Button,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  Typography as MuiTypography,
} from "@mui/material";
import { MobileDatePicker } from "@mui/x-date-pickers";
import { ActiveFilterControls } from "../components/active-filter-controls";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { UserContext } from "../components/app-routes";
import { unitSystemLabel } from "./crops-page";
import mixpanel from "mixpanel-browser";
import { getCustomLabel } from "../utils/custom-labels";

enum DateRange {
  Next7Days,
  Next30Days,
  RestOfYear,
  Today,
  Tomorrow,
  Custom,
  AllTime,
}

const DateRangeAllTimeFromDate = "2020-01-01";
const DateRangeAllTimeToDate = "2100-01-01";

const getDatesFromRange = (range: DateRange): { from: string; to: string } => {
  const today = DateTime.now().startOf("day");

  switch (range) {
    case DateRange.Next7Days:
      return {
        from: today.toISODate()!,
        to: today.plus({ days: 7 }).toISODate()!,
      };
    case DateRange.Next30Days:
      return {
        from: today.toISODate()!,
        to: today.plus({ days: 30 }).toISODate()!,
      };
    case DateRange.RestOfYear:
      return {
        from: today.toISODate()!,
        to: today.endOf("year").toISODate()!,
      };
    case DateRange.Today:
      return {
        from: today.toISODate()!,
        to: today.toISODate()!,
      };
    case DateRange.Tomorrow:
      return {
        from: today.plus({ days: 1 }).toISODate()!,
        to: today.plus({ days: 1 }).toISODate()!,
      };
    case DateRange.AllTime:
      return {
        from: DateRangeAllTimeFromDate,
        to: DateRangeAllTimeToDate,
      };
    default:
      return {
        from: today.toISODate()!,
        to: today.plus({ days: 30 }).toISODate()!,
      };
  }
};

export default function CropTrackerPage() {
  const renderTaskDateWithNav = (
    actual: string | undefined,
    expected: string | undefined,
    taskType: string,
    status?: string,
  ) => {
    const isRealDate = (s?: string) => !!s && s !== "-" && s !== "—";

    const actualDate = isRealDate(actual) ? actual : undefined;
    const expectedDate = isRealDate(expected) ? expected : undefined;

    const display = actualDate ?? expectedDate;
    if (!display) return <span>—</span>;

    const dt = DateTime.fromISO(display);
    if (!dt.isValid) return <span>—</span>;

    const isCompleted =
      status === "Complete" ||
      (!!actualDate && actualDate !== "-" && actualDate !== "—");

    const today = DateTime.now().startOf("day");
    const expectedDt = expectedDate
      ? DateTime.fromISO(expectedDate).startOf("day")
      : null;

    const isOverdue =
      !isCompleted && !!expectedDt && expectedDt.isValid && expectedDt < today;

    return (
      <Chip
        label={dt.toLocaleString(DateTime.DATE_MED)}
        size="small"
        sx={{
          backgroundColor: isCompleted
            ? "#c8e6c9"
            : isOverdue
              ? "#ffcdd2"
              : "#ffe0b2",
          color: "#000",
          fontSize: "0.75rem",
          cursor: "pointer",
          textDecoration: "underline",
        }}
        onClick={() => {
          history.push(`/home/tasks?date=${display}&task=${taskType}`);
        }}
      />
    );
  };

  const history = useHistory();
  const { grower } = useContext(UserContext);
  if (!grower) {
    throw new Error("Invalid context");
  }

  const columns: GridColDef[] = [
    {
      field: "sowDateSort",
      headerName: "Sow Sort",
      sortable: true,
      hideable: false,
      filterable: false,
      sortComparator: (a, b) => {
        if (!a && !b) return 0;
        if (!a) return 1;
        if (!b) return -1;

        return DateTime.fromISO(a).toMillis() - DateTime.fromISO(b).toMillis();
      },
    },
    {
      ...GRID_CHECKBOX_SELECTION_COL_DEF,
      hideable: false,
      headerName: "Checkbox",
      renderHeader: () => null,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
    },
    {
      field: "archived",
      headerName: "",
      width: 35,
      minWidth: 35,
      sortable: false,
      filterable: false,
      align: "center",
      disableColumnMenu: true,
      disableExport: true,
      renderCell: (params) =>
        params.row.archivedAt ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              height: "100%",
              overflow: "visible", // prevent ellipsis
            }}
          >
            <Inventory2OutlinedIcon fontSize="small" titleAccess="Archived" />
          </Box>
        ) : null,
    },
    {
      field: "cropName",
      headerName: "Crop",
      width: 200,
      headerAlign: "left",
      align: "left",
      sortable: true,
      sortComparator: (a, b) => a.localeCompare(b),
      renderCell: (params) => {
        const isLive = params.row.inventoryType === "live";

        return (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ fontSize: ".85rem" }}>{params.value}</span>

            <Chip
              label={isLive ? "LIVE" : "CUT"}
              size="small"
              sx={{
                height: 18,
                fontSize: "0.65rem",
                fontWeight: 600,
                backgroundColor: isLive ? "#6dbc11ff" : "#7dc2f3ff",
                color: isLive ? "#ffffffff" : "#ffffffff",
              }}
            />
          </div>
        );
      },
    },

    {
      field: "available",
      headerName: `Avail. (${unitSystemLabel(grower)} or tray)`,
      headerAlign: "center",
      align: "center",
      width: 85,
      minWidth: 85,
      renderCell: (params) => {
        const harvested = !!params.row.harvestDateActual;
        const v = params.row.available;

        if (v == null) {
          if (params.row.inventoryType === "live") {
            const trays = params.row.trays ?? 0;
            const extra = trays - trays; // yield - needed

            return (
              <span style={{ fontSize: "0.8125rem", color: "#000000" }}>
                {extra} tray{extra === 1 ? "" : "s"}
              </span>
            );
          }

          return "—";
        }

        // Not harvested => orange, unless negative (then red)
        if (!harvested) {
          return (
            <Chip
              label={Math.round(v)}
              size="small"
              sx={{
                backgroundColor: v < 0 ? "#ffcdd2" : "#ffe0b2", // red : orange
                fontWeight: v < 0 ? 600 : 500,
              }}
            />
          );
        }

        // Harvested => green if >= 0, red if < 0
        return (
          <Chip
            label={Math.round(v)}
            size="small"
            sx={{
              backgroundColor: v < 0 ? "#ffcdd2" : "#c8e6c9", // red : green
              fontWeight: 600,
            }}
          />
        );
      },
    },
    {
      field: "batchNumber",
      headerName: "Batch",
      headerAlign: "center",
      align: "center",
      width: 70,
      renderCell: (params) => {
        const value: string = params.value || "";
        const [part1, part2, part3] = value.split("-");

        return (
          <div
            style={{
              display: "flex", // ensure flex layout
              flexDirection: "column",
              justifyContent: "center", // vertical center
              alignItems: "center", // horizontal center
              height: "100%", // ensure it fills the cell
              fontSize: "0.75rem",
              lineHeight: 1.1,
              textAlign: "center",
            }}
          >
            {part1 && <div>{part1}-</div>}
            {part2 && <div>{part2}-</div>}
            {part3 && <div>{part3}</div>}
          </div>
        );
      },
    },
    {
      field: "status",
      headerName: "Stage",
      width: 105,
      minWidth: 105,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => {
        const value = params.value;

        let sx: any = {};

        switch (value) {
          case "InGermination":
            sx = { backgroundColor: "#000", color: "#fff" };
            break;
          case "Soaking":
            sx = { backgroundColor: "#0288d1", color: "#fff" };
            break;
          case "InBlackout":
            sx = { backgroundColor: "#e0e0e0", color: "#000" };
            break;
          case "InLight":
            sx = { backgroundColor: "#ffd181", color: "#000" };
            break;
          case "Complete":
            sx = { backgroundColor: "#5aab61", color: "#000" }; // light green
            break;
          default:
            sx = { backgroundColor: "#eeeeee", color: "#000" }; // fallback
            break;
        }

        return (
          <Chip
            label={
              value === "InGermination"
                ? "In Germ"
                : value.replace(/^In/, "In ")
            }
            size="small"
            sx={{ fontWeight: 500, ...sx }}
          />
        );
      },
    },
    {
      field: "seedLots",
      headerName: "Seed Lot",
      headerAlign: "center",
      align: "center",
      width: 90,
      minWidth: 90,
      renderCell: (params) => {
        const value = params.value;

        if (!value || value === "NaN") {
          return <span>—</span>;
        }

        return (
          <span style={{ fontSize: "0.75rem", color: "#333" }}>
            {String(value)}
          </span>
        );
      },
    },
    {
      field: "trays",
      headerName: "Trays",
      headerAlign: "center",
      align: "center",
      type: "number",
      width: 65,
      minWidth: 65,
      renderCell: (params) => {
        const value = params.value;
        return (
          <div
            style={{
              textAlign: "center",
              width: "100%",
              fontSize: "0.85rem", // ✅ Consistent font size
              fontWeight: value != null && value < 0 ? 600 : undefined,
              color: value != null && value < 0 ? "#b71c1c" : undefined,
            }}
          >
            {value != null ? Math.round(value) : "—"}
          </div>
        );
      },
    },

    {
      field: "soakDate",
      headerName: "Soak",
      headerAlign: "center",
      align: "center",
      width: 110,
      minWidth: 110,
      renderCell: (params) =>
        renderTaskDateWithNav(
          params.row.soakDateActual,
          params.row.soakDateExpected,
          "soak",
          params.row.status,
        ),
    },
    {
      field: "sowDate",
      headerName: "Sow & Cover",
      headerAlign: "center",
      align: "center",
      width: 110,
      minWidth: 110,
      renderCell: (params) =>
        renderTaskDateWithNav(
          params.row.sowDateActual,
          params.row.sowDateExpected,
          "sow",
          params.row.status,
        ),
    },
    {
      field: "blackoutDate",
      headerName: "Move to B/O",
      headerAlign: "center",
      align: "center",
      width: 110,
      minWidth: 110,
      renderCell: (params) => {
        // ⛔ Skipped blackout
        if (params.row.blackoutDays === 0) {
          return <span style={{ fontStyle: "italic", color: "#888" }}>—</span>;
        }

        return renderTaskDateWithNav(
          params.row.blackoutDateActual,
          params.row.blackoutDateExpected,
          "blackout",
          params.row.status,
        );
      },
    },
    {
      field: "uncoverDate",
      headerName: "Uncover",
      headerAlign: "center",
      align: "center",
      width: 110,
      minWidth: 110,
      renderCell: (params) =>
        renderTaskDateWithNav(
          params.row.uncoverDateActual,
          params.row.uncoverDateExpected,
          "uncover",
          params.row.status,
        ),
    },
    {
      field: "harvestDate",
      headerName: "Harvest",
      headerAlign: "center",
      align: "center",
      width: 110,
      minWidth: 110,
      renderCell: (params) =>
        renderTaskDateWithNav(
          params.row.harvestDateActual,
          params.row.harvestDateExpected,
          "harvest",
          params.row.status,
        ),
    },
    {
      field: "yield",
      headerName: `Yield (${unitSystemLabel(grower)})`,
      headerAlign: "center",
      align: "center",
      width: 80,
      minWidth: 80,
      renderCell: (params) => {
        const harvested = !!params.row.harvestDateActual;

        // show actual if harvested, else expected
        const v = harvested ? params.row.actualYield : params.row.expectedYield;

        if (v == null) {
          if (params.row.inventoryType === "live") {
            const trays = params.row.trays ?? 0;
            return (
              <span style={{ fontSize: "0.8125rem", color: "#000000" }}>
                {trays} tray{trays === 1 ? "" : "s"}
              </span>
            );
          }

          return "—";
        }

        return (
          <Chip
            label={Math.round(v)}
            size="small"
            sx={{
              backgroundColor: harvested ? "#c8e6c9" : "#ffe0b2", // green : orange
              fontWeight: 500,
            }}
          />
        );
      },
    },
    {
      field: "quantityNeeded",
      headerName: `Needed (${unitSystemLabel(grower)} or trays)`,
      headerAlign: "center",
      align: "center",
      width: 105,
      renderCell: (params) => {
        const q = params.row.quantityNeeded;
        const harvested = !!params.row.harvestDateActual;

        if (q == null) {
          if (params.row.inventoryType === "live") {
            const trays = params.row.trays ?? 0;
            return (
              <span style={{ fontSize: "0.8125rem", color: "#000000" }}>
                {trays} tray{trays === 1 ? "" : "s"}
              </span>
            );
          }

          return "—";
        }

        return (
          <Chip
            label={Math.round(q)}
            size="small"
            sx={{
              backgroundColor: harvested ? "#c8e6c9" : "#ffe0b2", // green : orange
              fontWeight: 500,
            }}
          />
        );
      },
    },
    {
      field: "stageOrder",
      headerName: "Stage Order",
      headerAlign: "center",
      align: "center",
      type: "number",
      width: 100,
      hideable: false,
      sortable: true,
    },
  ];

  const sowColumnOptions = columns
    .filter(
      (col: GridColDef) =>
        col.field !== "sowDateSort" && col.field !== "stageOrder",
    )
    .map((col: GridColDef) => ({
      field: col.field,
      label: col.headerName || col.field,
    }));

  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    columns
      .map((col: GridColDef) => col.field)
      .filter(
        (field: string) => field !== "sowDateSort" && field !== "stageOrder",
      ),
  );

  const [rows, setRows] = useState<CropTrackerRow[]>([]);
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const raw = window.localStorage.getItem("cropTrackerDateRange");
    return raw ? Number(raw) : DateRange.Next30Days;
  });
  const handleChangeDateRange = (event: SelectChangeEvent<number>) => {
    setDateRange(event.target.value as DateRange);
  };

  const [customFrom, setCustomFrom] = useState<string>(() => {
    return (
      window.localStorage.getItem("cropTrackerCustomFrom") ||
      DateTime.now().startOf("day").toISODate()!
    );
  });

  const [customTo, setCustomTo] = useState<string>(() => {
    return (
      window.localStorage.getItem("cropTrackerCustomTo") ||
      DateTime.now().plus({ days: 30 }).toISODate()!
    );
  });

  const handleFromDateChange = (date: DateTime | null) => {
    if (!date) return;

    const iso = date.startOf("day").toISODate()!;
    setCustomFrom(iso);
    setDateRange(DateRange.Custom);

    window.localStorage.setItem("cropTrackerCustomFrom", iso);
  };

  const handleToDateChange = (date: DateTime | null) => {
    if (!date) return;
    const iso = date.startOf("day").toISODate()!;
    setCustomTo(iso);
    setDateRange(DateRange.Custom);
    window.localStorage.setItem("cropTrackerCustomTo", iso);
  };

  const [selection, setSelection] = useState<GridRowSelectionModel>({
    type: "include",
    ids: new Set(),
  });

  const gridRows = React.useMemo(() => {
    return rows.map((row) => {
      const stageSortOrder: Record<string, number> = {
        Soaking: 1,
        InGermination: 2,
        InBlackout: 3,
        InLight: 4,
        Complete: 5,
      };

      const resolveDate = (actual?: string, expected?: string) => {
        if (actual && actual !== "-" && actual !== "—") return actual;
        if (expected && expected !== "-" && expected !== "—") return expected;
        return null;
      };

      const resolveAnchorDate = (
        actual?: string,
        expected?: string,
      ): string | null => {
        if (actual && actual !== "-" && actual !== "—") return actual;
        if (expected && expected !== "-" && expected !== "—") return expected;
        return null;
      };

      return {
        id:
          row.batchNumber && row.batchNumber !== ""
            ? `${row.cropId}-${row.batchNumber}-${row.inventoryType ?? "cut"}`
            : `${row.cropId}-prebatch-${(row as any).cropCycleId ?? "x"}-${row.inventoryType ?? "cut"}`,
        cropCycleId: (row as any).cropCycleId ?? null,
        inventoryType: row.inventoryType ?? "cut",
        archivedAt: (row as any).archivedAt ?? null,

        sowDateSort:
          resolveDate(row.sowDateActual, row.sowDateExpected) ?? "9999-12-31",

        cropName: row.cropName,
        sowDate: resolveDate(row.sowDateActual, row.sowDateExpected),

        // 👇 this is what we’ll filter on later
        sowAnchorDate: resolveAnchorDate(
          row.sowDateActual,
          row.sowDateExpected,
        ),

        harvestAnchorDate: resolveAnchorDate(
          row.harvestDateActual,
          row.harvestDateExpected,
        ),

        batchNumber: row.batchNumber,
        seedLots: row.seedLots,
        status: row.status,
        trays: row.trays,

        stageOrder: stageSortOrder[row.status] ?? 99,

        soakDate: resolveDate(row.soakDateActual, row.soakDateExpected),
        blackoutDate: resolveDate(
          row.blackoutDateActual,
          row.blackoutDateExpected,
        ),
        uncoverDate: resolveDate(
          row.uncoverDateActual,
          row.uncoverDateExpected,
        ),
        harvestDate: resolveDate(
          row.harvestDateActual,
          row.harvestDateExpected,
        ),

        quantityNeeded: (row as any).quantityNeeded ?? null,

        expectedYield: row.expectedYield ?? null,
        actualYield: row.actualYield ?? null,
        available: row.available ?? null,

        isOverdue: row.isOverdue,
        firstIncompleteTaskType: row.firstIncompleteTaskType,
        firstIncompleteDate: row.firstIncompleteDate,

        soakDateActual: row.soakDateActual,
        soakDateExpected: row.soakDateExpected,

        sowDateActual: row.sowDateActual,
        sowDateExpected: row.sowDateExpected,

        blackoutDateActual: row.blackoutDateActual,
        blackoutDateExpected: row.blackoutDateExpected,

        uncoverDateActual: row.uncoverDateActual,
        uncoverDateExpected: row.uncoverDateExpected,

        harvestDateActual: row.harvestDateActual,
        harvestDateExpected: row.harvestDateExpected,
      };
    });
  }, [rows]);

  const selectedRows = React.useMemo(() => {
    if (!selection) return [];

    const ids = Array.isArray(selection)
      ? new Set(selection.map(String))
      : new Set(Array.from((selection as any).ids).map(String));

    return gridRows.filter((r: any) => ids.has(String(r.id)));
  }, [gridRows, selection]);

  const selectedCropCycleIds = React.useMemo(() => {
    return selectedRows
      .map((r: any) => r.cropCycleId)
      .filter((id): id is number => typeof id === "number");
  }, [selectedRows]);

  const hasActiveSelected = selectedRows.some((r: any) => !r.archivedAt);

  const hasArchivedSelected = selectedRows.some((r: any) => r.archivedAt);

  const showArchivedCheckedStorageKey = "cropTrackerShowArchivedChecked";
  const showActiveCheckedStorageKey = "cropTrackerShowActiveChecked";

  const [busy, setBusy] = useState(false);

  const [showArchivedChecked, setShowArchivedChecked] = useState(
    window.localStorage.getItem(showArchivedCheckedStorageKey) !==
      false.toString(),
  );

  const [showActiveChecked, setShowActiveChecked] = useState(
    window.localStorage.getItem(showActiveCheckedStorageKey) !==
      false.toString(),
  );

  const handleShowActiveChanged = (checked: boolean) => {
    setShowActiveChecked(checked);
    window.localStorage.setItem(
      showActiveCheckedStorageKey,
      checked.toString(),
    );
  };

  const handleShowArchivedChanged = (checked: boolean) => {
    setShowArchivedChecked(checked);
    window.localStorage.setItem(
      showArchivedCheckedStorageKey,
      checked.toString(),
    );
  };

  const clearSelection = React.useCallback(() => {
    const empty: GridRowSelectionModel = { type: "include", ids: new Set() };
    setSelection(empty);
  }, []);

  const handleArchive = async () => {
    if (selectedCropCycleIds.length === 0) return;

    setBusy(true);
    try {
      await cropTrackerApi.archive(selectedCropCycleIds);

      // (we’ll make this faster in step 2)
      const updated = await cropTrackerApi.getAll();
      setRows(updated);

      clearSelection();
    } finally {
      setBusy(false);
    }
  };

  const handleUnarchive = async () => {
    if (selectedCropCycleIds.length === 0) return;

    setBusy(true);
    try {
      await cropTrackerApi.unarchive(selectedCropCycleIds);

      const updated = await cropTrackerApi.getAll();
      setRows(updated);

      clearSelection();
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    window.localStorage.setItem("cropTrackerDateRange", dateRange.toString());
  }, [dateRange]);

  const USE_FAKE_CROP_TRACKER_DATA = false;

  const { handleError } = useContext(ErrorContext);

  const fakeCropTrackerRows: CropTrackerRow[] = [
    {
      cropId: 1,
      cropName: "Pea Shoots",
      batchNumber: "PEA-031025-031725",
      seedLots: "Lot A",
      trays: 12,
      soakDate: "2025-03-09",
      sowDate: "2025-03-10",
      blackoutDate: "2025-03-12",
      uncoverDate: "2025-03-15",
      harvestDate: "2025-03-17",
      soakDateActual: "2025-03-09",
      sowDateActual: "2025-03-10",
      blackoutDateActual: "2025-03-12",
      uncoverDateExpected: "2025-03-15",
      harvestDateExpected: "2025-03-17",
      expectedYield: 24,
      actualYield: 22,
      available: -2,
      status: "InLight",
      blackoutDays: 2,
    },
    {
      cropId: 2,
      cropName: "Radish",
      batchNumber: "RAD-031125-031825",
      seedLots: "Lot C",
      trays: 8,
      soakDate: "",
      sowDate: "2025-03-11",
      blackoutDate: "2025-03-13",
      uncoverDate: "2025-03-15",
      harvestDate: "2025-03-18",
      sowDateActual: "2025-03-11",
      blackoutDateActual: "2025-03-13",
      uncoverDateActual: "2025-03-15",
      harvestDateExpected: "2025-03-18",
      expectedYield: 16,
      actualYield: undefined,
      available: 16,
      status: "InLight",
      blackoutDays: 2,
    },
  ];

  useEffect(() => {
    mixpanel.track("Visit Crop Tracker");

    if (USE_FAKE_CROP_TRACKER_DATA) {
      setRows(fakeCropTrackerRows);
      return;
    }

    cropTrackerApi
      .getAll()
      .then(setRows)
      .catch((err) => {
        handleError("Failed to load crop tracker data");
        console.error(err);
      });
  }, []);

  const filteredGridRows = React.useMemo(() => {
    if (!gridRows.length) return gridRows;

    const { from, to } =
      dateRange === DateRange.Custom
        ? { from: customFrom, to: customTo }
        : getDatesFromRange(dateRange);

    const fromDate = DateTime.fromISO(from);
    const toDate = DateTime.fromISO(to);

    if (!fromDate.isValid || !toDate.isValid) return gridRows;

    return gridRows.filter((row: any) => {
      // ✅ Archived rows bypass date filtering
      if (row.archivedAt) return true;

      const sowDate = row.sowAnchorDate
        ? DateTime.fromISO(row.sowAnchorDate)
        : null;

      const harvestDate = row.harvestAnchorDate
        ? DateTime.fromISO(row.harvestAnchorDate)
        : null;

      // fallback handling
      if (sowDate?.isValid && harvestDate?.isValid) {
        return sowDate <= toDate && harvestDate >= fromDate;
      }

      if (harvestDate?.isValid) {
        return harvestDate >= fromDate && harvestDate <= toDate;
      }

      if (sowDate?.isValid) {
        return sowDate >= fromDate && sowDate <= toDate;
      }

      return false;
    });
  }, [gridRows, dateRange, customFrom, customTo]);

  const visibleGridRows = React.useMemo(() => {
    const filtered = filteredGridRows.filter((row: any) => {
      if (row.archivedAt) return showArchivedChecked;
      return showActiveChecked;
    });

    return filtered.sort((a, b) => {
      const dateA = a.harvestDate ?? "9999-12-31";
      const dateB = b.harvestDate ?? "9999-12-31";

      const cmpDate =
        DateTime.fromISO(dateA).toMillis() - DateTime.fromISO(dateB).toMillis();

      if (cmpDate !== 0) return cmpDate;

      return a.cropName.localeCompare(b.cropName);
    });
  }, [filteredGridRows, showActiveChecked, showArchivedChecked]);

  if (!Array.isArray(rows)) {
    return (
      <Page title={getCustomLabel(grower ?? null, "cropTracker")}>
        {" "}
        <div style={{ padding: "24px" }}>Failed to load crop tracker data.</div>
      </Page>
    );
  }

  return (
    <Page title={getCustomLabel(grower ?? null, "cropTracker")}>
      {" "}
      {/* Header controls */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          mb: 2,
        }}
      >
        <FormControl variant="standard" sx={{ minWidth: 160 }}>
          <Select<number>
            variant="standard"
            value={dateRange}
            onChange={handleChangeDateRange}
          >
            <MenuItem value={DateRange.Next7Days}>Next 7 days</MenuItem>
            <MenuItem value={DateRange.Next30Days}>Next 30 days</MenuItem>
            <MenuItem value={DateRange.RestOfYear}>Rest of Year</MenuItem>
            <MenuItem value={DateRange.Today}>Today</MenuItem>
            <MenuItem value={DateRange.Tomorrow}>Tomorrow</MenuItem>
            <MenuItem value={DateRange.AllTime}>All Time</MenuItem>
            <MenuItem value={DateRange.Custom}>Custom</MenuItem>
          </Select>
        </FormControl>

        <Box
          onClick={() => {
            if (busy) return;
            if (!hasActiveSelected) return;
            handleArchive();
          }}
          sx={{
            cursor: !busy && hasActiveSelected ? "pointer" : "default",
            color:
              !busy && hasActiveSelected ? "text.primary" : "text.disabled",
            opacity: busy ? 0.5 : 1,
          }}
        >
          <Inventory2OutlinedIcon
            fontSize="medium"
            titleAccess="Archive selected batches"
          />
        </Box>

        {showArchivedChecked && (
          <Box
            onClick={() => {
              if (busy) return;
              if (!hasArchivedSelected) return;
              handleUnarchive();
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              pl: 1,
              cursor: !busy && hasArchivedSelected ? "pointer" : "default",
              color:
                !busy && hasArchivedSelected ? "text.primary" : "text.disabled",
              opacity: busy ? 0.5 : 1,
            }}
          >
            <Inventory2OutlinedIcon
              fontSize="medium"
              sx={{ transform: "rotate(180deg)" }}
              titleAccess="Unarchive selected batches"
            />
          </Box>
        )}

        <ActiveFilterControls
          showActiveChecked={showActiveChecked}
          showArchivedChecked={showArchivedChecked}
          onChangeShowActive={handleShowActiveChanged}
          onChangeShowArchived={handleShowArchivedChanged}
        />
      </Box>
      {dateRange === DateRange.Custom && (
        <Box sx={{ mb: 2 }}>
          <MobileDatePicker
            closeOnSelect
            label="From"
            value={DateTime.fromISO(customFrom)}
            onChange={handleFromDateChange}
            format="MMM d, yyyy"
            slotProps={{
              toolbar: { hidden: true },
              textField: {
                variant: "outlined",
                inputProps: { readOnly: true },
                sx: { mr: 1 },
              },
            }}
          />

          <MobileDatePicker
            closeOnSelect
            label="To"
            value={DateTime.fromISO(customTo)}
            onChange={handleToDateChange}
            format="MMM d, yyyy"
            slotProps={{
              toolbar: { hidden: true },
              textField: {
                variant: "outlined",
                inputProps: { readOnly: true },
              },
            }}
          />
        </Box>
      )}
      <div style={{ height: "750px", width: "100%", paddingTop: "16px" }}>
        <StripedDataGrid
          storageKey="crop-tracker-grid"
          onSortModelChange={() => {}}
          getRowId={(row) =>
            (row as any).id ??
            `${row.batchNumber}-${row.inventoryType ?? "cut"}`
          }
          getRowClassName={(params) => {
            if ((params.row as any).archivedAt) return "archived-row";
            return params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd";
          }}
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              minHeight: "64px !important",
              maxHeight: "64px !important",
            },
            "& .MuiDataGrid-columnHeader": {
              height: "64px !important",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              whiteSpace: "normal",
              lineHeight: 1.2,
              textAlign: "center",
            },
            "& .even": {
              backgroundColor: "#fafafa",
            },
            "& .odd": {
              backgroundColor: "#fff",
            },
            "& .expected-task-cell": {
              backgroundColor: "#fff3e0",
              color: "#bf360c",
              fontStyle: "italic",
              textAlign: "center",
            },
            "& .missing-task-cell": {
              backgroundColor: "#ffebee",
              color: "#b71c1c",
              fontWeight: 500,
              textAlign: "center",
            },
            "& .skipped-task-cell": {
              textAlign: "center",
              fontStyle: "italic",
              color: "#888",
              "& .odd": {
                backgroundColor: "#fff",
              },
              "& .archived-row": {
                opacity: 0.55,
              },
            },
          }}
          checkboxSelection
          disableMultipleRowSelection={false}
          disableRowSelectionOnClick
          onRowSelectionModelChange={(m) => {
            setSelection(m);
          }}
          columns={columns}
          rows={visibleGridRows}
          initialState={{
            sorting: {
              sortModel: [
                {
                  field: "sowDate",
                  sort: "asc",
                },
              ],
            },
          }}
          pagination
          paginationMode="client"
          pageSizeOptions={[15, 25, 50, 100]}
          showToolbar
          disableColumnFilter={false}
          disableColumnSelector={false}
          disableDensitySelector={false}
          slots={{
            toolbar: GridToolbar,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              csvOptions: { allColumns: true },
            },
          }}
        />
      </div>
      <LoadingBackdrop open={busy} />
    </Page>
  );
}
